/* The Prayer screen.
 *
 * The client's three tabs, in their wording:
 *   All Prayer Requests | My Requests | My Prayers
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTabParam } from '@/lib/useTabParam'
import { Gift, Loader2, Plus, Star } from 'lucide-react'
import * as Tabs from '@radix-ui/react-tabs'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import {
  PhotoButton,
  PhotoGrid,
  usePhotoAttachments,
} from '@/features/feed/PhotoPicker'
import {
  useCreatePrayerRequest,
  useHelpOptions,
  useHelpUnseen,
  useMarkHelpSeen,
  useMe,
  usePrayerRequests,
} from '@/api/queries'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { cn } from '@/lib/utils'
import { HelpDialog } from './HelpDialog'
import { PrayerCard } from './PrayerCard'
import { useFocusTarget } from '@/features/notifications/useFocusTarget'
import { usePrayerRequest } from '@/api/queries'
import type { PrayerState, PrayerTab } from '@/types/api'

const TABS: { value: PrayerTab; label: string }[] = [
  { value: 'all', label: 'All Prayer Requests' },
  { value: 'mine', label: 'My Requests' },
  { value: 'praying', label: 'My Prayers' },
]

export function PrayerPage() {
  const [tab, setTab] = useTabParam<PrayerTab>(
    TABS.map((t) => t.value),
    'all',
  )
  const [helpOpen, setHelpOpen] = useState(false)
  const helpUnseen = useHelpUnseen()
  const markHelpSeen = useMarkHelpSeen()
  const unseen = helpUnseen.data?.unseen_count ?? 0
  const [composeOpen, setComposeOpen] = useState(false)

  return (
    <div>
      {/* The client's header row: the title, with Follow and Help to the
          right of the tabs (wireframe, Prayer screen). */}
      <div className="mb-s4 flex items-center gap-s3">
        <h1 className="text-2xl font-bold tracking-tight">Prayer Request</h1>
        <div className="ml-auto flex gap-s2">
          <Link
            to="/prayer/following"
            className="flex items-center gap-1 rounded-pill bg-surface px-s3 py-1.5 text-sm font-semibold text-ink-2 shadow-1 transition-colors hover:bg-hover"
          >
            <Star className="h-3.5 w-3.5" aria-hidden />
            Following
          </Link>
          {/* The badge is what makes a standing offer worth making. Without
              it somebody ticks "I can provide medicines" and only finds out
              by reopening the dialog themselves. */}
          <button
            onClick={() => {
              setHelpOpen(true)
              if (unseen) markHelpSeen.mutate()
            }}
            aria-label={
              unseen
                ? `Help, ${unseen} ${unseen === 1 ? 'person needs' : 'people need'} what you offer`
                : 'Help'
            }
            className="flex items-center gap-1 rounded-pill bg-surface px-s3 py-1.5 text-sm font-semibold text-ink-2 shadow-1 transition-colors hover:bg-hover"
          >
            <Gift className="h-3.5 w-3.5" aria-hidden />
            Help
            {unseen ? (
              <span className="ml-0.5 grid h-4 min-w-4 place-items-center rounded-pill bg-brand px-1 text-[11px] font-bold text-white">
                {unseen > 9 ? '9+' : unseen}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      <PrayerComposer open={composeOpen} setOpen={setComposeOpen} />

      <Tabs.Root
        value={tab}
        onValueChange={(v) => setTab(v as PrayerTab)}
        className="mt-s4"
      >
        <Tabs.List
          className="mb-s4 flex gap-1 rounded-md bg-surface p-1 shadow-1"
          aria-label="Prayer Request tabs"
        >
          {TABS.map(({ value, label }) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className={cn(
                'flex-1 rounded px-s2 py-s2 text-sm font-semibold transition-colors',
                'data-[state=active]:bg-brand-soft data-[state=active]:text-brand',
                'data-[state=inactive]:text-ink-2 data-[state=inactive]:hover:bg-hover',
              )}
            >
              {label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {TABS.map(({ value }) => (
          <Tabs.Content key={value} value={value} className="outline-none">
            {/* Mounted only while selected, so switching tabs does not keep
                three infinite scrollers alive at once. */}
            {tab === value ? (
              <PrayerList tab={value} onCompose={() => setComposeOpen(true)} />
            ) : null}
          </Tabs.Content>
        ))}
      </Tabs.Root>

      {helpOpen ? <HelpDialog onClose={() => setHelpOpen(false)} /> : null}
    </div>
  )
}

function PrayerList({
  tab,
  onCompose,
}: {
  tab: PrayerTab
  onCompose: () => void
}) {
  const { data: me } = useMe()
  const list = usePrayerRequests(tab)
  /* Which request and comment the bell sent us to, if any. */
  const focus = useFocusTarget('request')

  const sentinel = useInfiniteScroll(() => list.fetchNextPage(), {
    enabled: list.hasNextPage && !list.isFetchingNextPage,
  })

  const loaded = list.data?.pages.flatMap((p) => p.items) ?? []

  /* The request a notification pointed at, lifted to the top — the same shape
     as the feed's pinned post. Not a page of its own, and not scrolled to: it
     can be far down the list or filtered out by whichever tab is open. */
  const pinned = usePrayerRequest(focus.entityId)
  const items = pinned.data
    ? [pinned.data, ...loaded.filter((r) => r.id !== pinned.data!.id)]
    : loaded

  if (list.isLoading) {
    return (
      <div className="space-y-s4">
        <Skeleton className="h-32 w-full rounded-md" />
        <Skeleton className="h-32 w-full rounded-md" />
      </div>
    )
  }

  if (list.isError) {
    return (
      <div className="cn-card p-s6 text-center">
        <p className="font-semibold">These did not load.</p>
        <Button variant="secondary" className="mt-s4" onClick={() => list.refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  if (!items.length) {
    return (
      <div className="cn-card p-s8 text-center">
        <p className="text-md font-bold">
          {tab === 'mine'
            ? 'You have not posted a Prayer Request yet'
            : tab === 'praying'
              ? 'You are not praying for anyone yet'
              : 'No Prayer Requests yet'}
        </p>
        <p className="mt-1 text-base text-ink-2">
          {tab === 'praying'
            ? 'Requests you commit to pray for will gather here.'
            : 'Share what you need and your church can pray with you.'}
        </p>

        {/* The button, not a sentence pointing at one. "Post one above" sends
            someone hunting for a control that is off-screen on a tall
            display, which is exactly what happened. */}
        {tab !== 'praying' ? (
          <Button variant="primary" className="mt-s4" onClick={onCompose}>
            <Plus className="h-4 w-4" aria-hidden />
            Post a Prayer Request
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div className="space-y-s4">
      {items.map((request) => (
        <PrayerCard
              key={request.id}
              request={request}
              viewerId={me?.id}
              focusComment={
                focus.entityId === request.id ? focus.commentId : null
              }
              highlighted={focus.entityId === request.id}
            />
      ))}

      <div ref={sentinel} className="h-px" aria-hidden />

      {list.isFetchingNextPage ? (
        <div className="flex justify-center py-s4">
          <Loader2 className="h-5 w-5 animate-spin text-ink-3" aria-label="Loading more" />
        </div>
      ) : null}
    </div>
  )
}

function PrayerComposer({
  open,
  setOpen,
}: {
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const { data: me } = useMe()

  if (!me) return null

  return (
    <>
      <Card className="flex items-center gap-s3 p-s3">
        <Avatar name={me.first_name} src={me.avatar_url} />
        <button
          onClick={() => setOpen(true)}
          className="h-10 min-w-0 flex-1 truncate rounded-pill bg-surface-2 px-s4 text-left text-md text-ink-2 transition-colors hover:bg-hover-2"
        >
          Post your prayer.. It helps you to strengthen your faith.
        </button>
        {/* A prompt that looks like a text field reads as somewhere to type,
            not as the way to start. The button says what it does. */}
        <Button
          variant="primary"
          onClick={() => setOpen(true)}
          className="hidden shrink-0 sm:flex"
        >
          <Plus className="h-4 w-4" aria-hidden />
          Post
        </Button>
      </Card>

      {open ? <PrayerComposerModal onClose={() => setOpen(false)} /> : null}
    </>
  )
}

/* The same modal as the feed composer, with the client's state picker in place
   of the visibility chips. Two composers, one interaction — someone who has
   posted once already knows how this works. */
function PrayerComposerModal({ onClose }: { onClose: () => void }) {
  const [body, setBody] = useState('')
  // Defaults to Red: someone posting a request is asking for prayer now.
  const [state, setState] = useState<PrayerState>('red')
  /* What practical help would meet this need — the author's own tag.
     OPTIONAL, and it stays optional: most requests are for prayer alone, and
     making somebody categorise their grief to post it would be worse than no
     tag at all. But without it the Help feature has nothing to match on —
     every request fell into the "untagged matches everything" branch, which is
     why ticking "I can hire" returned prayers about exams. */
  const [helpKind, setHelpKind] = useState<string | null>(null)
  const [showHelpKinds, setShowHelpKinds] = useState(false)

  const { data: me } = useMe()
  const create = useCreatePrayerRequest()
  const photos = usePhotoAttachments()
  const helpOptions = useHelpOptions()

  const hasContent = Boolean(body.trim()) || photos.hasAny

  if (!me) return null

  function submit() {
    if (!hasContent || create.isPending || photos.isUploading) return
    create.mutate(
      {
        body: body.trim(),
        state,
        media_ids: photos.mediaIds,
        help_kind: helpKind,
      },
      {
        onSuccess: () => {
          photos.reset()
          onClose()
        },
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) {
          photos.reset()
          onClose()
        }
      }}
      title="Post a Prayer Request"
      guardClose={hasContent}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={submit}
          disabled={!hasContent || photos.isUploading}
          loading={create.isPending}
        >
          {photos.videoProgress !== null
            ? `Uploading video ${Math.round(photos.videoProgress * 100)}%`
            : photos.isUploading
              ? 'Uploading…'
              : 'Post'}
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <div className="flex items-center gap-s3">
          <Avatar name={me.first_name} src={me.avatar_url} />
          <div>
            <div className="text-md font-semibold">
              {me.first_name} {me.last_name ?? ''}
            </div>
            {/* The client's colour code, chosen at posting time. */}
            <div className="mt-1 flex gap-1">
              {(Object.keys(STATE_LABELS) as PrayerState[]).map((key) => (
                <button
                  key={key}
                  onClick={() => setState(key)}
                  aria-pressed={state === key}
                  className={cn(
                    'rounded-sm px-s2 py-0.5 text-sm font-semibold transition-colors',
                    state === key
                      ? STATE_LABELS[key].on
                      : 'bg-surface-2 text-ink-2 hover:bg-hover-2',
                  )}
                >
                  {STATE_LABELS[key].label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 5000))}
          placeholder="Post your prayer.. It helps you to strengthen your faith."
          aria-label="Your Prayer Request"
          rows={4}
          autoFocus
          className="w-full resize-none border-none bg-transparent text-lg outline-none placeholder:text-ink-2"
        />

        <PhotoGrid items={photos.items} onRemove={photos.remove} />

        <PhotoButton
          onPick={photos.add}
          disabled={photos.isFull}
          label={
            photos.items.some((i) => i.kind === 'video')
              ? 'One video per request'
              : photos.items.length
                ? 'Add another photo'
                : 'Add photos or a video'
          }
        />

        {/* The author's own tag. Same question and the same chips as the
            mobile composer, which had this and web did not — which is why
            nothing was ever tagged.

            need_label, not label: this is the person ASKING. "I can provide
            Groceries" here would have them offering the very thing they need. */}
        {/* COLLAPSED UNTIL WANTED.
            Ten chips laid out permanently made an optional field the heaviest
            thing on the dialog, above the Post button — and most requests are
            for prayer alone. One quiet line asks; the options appear only if
            the answer is yes. */}
        {helpOptions.data?.length ? (
          <div className="border-t border-line-2 pt-s3">
            {helpKind || showHelpKinds ? (
              <>
                <div className="flex items-center justify-between gap-s3">
                  <p className="text-sm font-semibold">
                    What would help, besides prayer?
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setHelpKind(null)
                      setShowHelpKinds(false)
                    }}
                    className="shrink-0 text-sm font-semibold text-ink-2 hover:text-ink"
                  >
                    Skip
                  </button>
                </div>
                <div className="mt-s2 flex flex-wrap gap-s2">
                  {helpOptions.data.map((o) => {
                    const on = helpKind === o.kind
                    return (
                      <button
                        key={o.kind}
                        type="button"
                        aria-pressed={on}
                        // Tapping the chosen one clears it — the way back to
                        // "no tag" without closing the whole section.
                        onClick={() => setHelpKind(on ? null : o.kind)}
                        className={cn(
                          'rounded-pill border px-s3 py-1.5 text-sm font-semibold transition-colors',
                          on
                            ? 'border-brand bg-brand-softer text-brand'
                            : 'border-line-2 text-ink-2 hover:bg-hover',
                        )}
                      >
                        {o.need_label}
                      </button>
                    )
                  })}
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setShowHelpKinds(true)}
                className="text-sm font-semibold text-brand hover:underline"
              >
                Would practical help meet this need?
              </button>
            )}
          </div>
        ) : null}

        {create.isError ? (
          <p className="text-sm font-semibold text-danger">
            {(create.error as Error)?.message ?? 'That did not send.'}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

/* Short labels for the picker. The full meanings live on the card. */
const STATE_LABELS: Record<PrayerState, { label: string; on: string }> = {
  red: { label: 'Critical', on: 'bg-pr-red-soft text-pr-red' },
  yellow: { label: 'Long term', on: 'bg-pr-yellow-soft text-pr-yellow-ink' },
  green: { label: 'Answered', on: 'bg-pr-green-soft text-pr-green-ink' },
}
